import { z } from "zod";
import type { TaskStatus, TaskPriority } from "@/lib/core/crm/types";
import {
  TASK_STATUS_VALUES,
  TASK_PRIORITY_VALUES,
  TASK_NOTE_TYPE_VALUES,
} from "@/lib/constants/task-enums";

// ============================================================================
// TASK ZOD SCHEMAS
// ============================================================================

// Task Status and Priority Schemas
export const TaskStatusSchema = z.enum(TASK_STATUS_VALUES);
export const TaskPrioritySchema = z.enum(TASK_PRIORITY_VALUES);

// Base Task Schema
export const TaskSchema = z.object({
  id: z.string(),
  contact_id: z.string().nullable(),
  user_id: z.string(),
  title: z.string().min(1, "Title is required").max(255, "Title too long"),
  description: z.string().optional(),
  status: TaskStatusSchema,
  priority: TaskPrioritySchema,
  due_date: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
  assigned_users: z.array(z.string()).optional(), // Multi-user assignment
  tags: z.array(z.string()).optional(),
  created_at: z.string(),
  updated_at: z.string(),
  entity_type: z.string().nullable().optional(), // Generic entity type (contacts, deals, companies, etc.)
  entity_id: z.string().nullable().optional(), // Generic entity ID (UUID)
  ticket_id: z.string().uuid().nullable().optional(),
  company_id: z.string().uuid().nullable().optional(),
  // Relations (optional for API responses)
  contacts: z
    .object({
      id: z.string(),
      name: z.string(),
      company_name: z.string().optional(),
      email: z.string().email(),
    })
    .optional(),
  users: z
    .object({
      id: z.string(),
      first_name: z.string(),
      last_name: z.string(),
      email: z.string().email(),
    })
    .optional(),
  assigned_user: z
    .object({
      id: z.string(),
      first_name: z.string(),
      last_name: z.string(),
      email: z.string().email(),
    })
    .optional(),
  // Task notes relationship
  task_notes: z
    .array(
      z.object({
        id: z.string(),
        task_id: z.string(),
        user_id: z.string(),
        note_type: z.string(),
        content: z.string(),
        is_internal: z.boolean(),
        created_at: z.string(),
        updated_at: z.string(),
        // User who created the note
        user: z
          .object({
            id: z.string(),
            first_name: z.string(),
            last_name: z.string(),
            email: z.string().email(),
          })
          .optional(),
      })
    )
    .optional(),
});

// Create Task Form Schema
export const CreateTaskFormSchema = z.object({
  title: z.string().min(1, "Title is required").max(255, "Title too long"),
  contact_id: z.string().nullable().optional(), // Made optional to allow general tasks
  user_id: z.string().min(1, "User ID is required"), // Required for task creation
  description: z.string().optional(),
  status: TaskStatusSchema.optional().default("pending"),
  priority: TaskPrioritySchema.optional().default("medium"),
  due_date: z.string().nullable().optional(),
  assigned_users: z.array(z.string()).optional(), // Multi-user assignment
  tags: z.array(z.string()).optional(),
  entity_type: z.string().nullable().optional(), // Generic entity type
  entity_id: z.string().nullable().optional(), // Generic entity ID
  company_id: z.string().uuid().nullable().optional(),
  ticket_id: z.string().uuid().nullable().optional(),
});

// Update Task Form Schema
//
// NOT a bare `.partial()`. `status` and `priority` on `CreateTaskFormSchema`
// carry `.default(...)`, and `.partial()` builds an update field by calling
// `.optional()` on the EXISTING (already-defaulted) field type — which in
// Zod v4 does not short-circuit an omitted key back to `undefined` the way
// Zod v3's `ZodOptional` did; it still substitutes the create-time default
// (verified directly: `z.string().default("pending").optional()
// .parse(undefined)` returns `"pending"`, not `undefined`). Left as
// `.partial()` alone, EVERY partial task update resets status to "pending"
// and priority to "medium" whenever the caller omits them — which every real
// caller of `useUpdateTask()` does except the ones setting status/priority
// directly (`ResponsiveUserAssignmentModal`/`useTaskActions` patch only
// `assigned_users` or `due_date`). `useUpdateTask()` below spreads the parsed
// result straight into the Supabase `.update(...)` call with no
// supplied-keys whitelist, so a resurrected default here is indistinguishable
// from a value the caller actually sent. Override just those two fields with
// `.removeDefault().optional()` so "omitted" means "leave alone," and leave
// every other field on `.partial()`, which is correct for them (none of them
// carries a default to resurrect). `CreateTaskFormSchema` itself is
// untouched, so creating a task with no status/priority supplied still gets
// the create-time defaults.
export const UpdateTaskFormSchema = CreateTaskFormSchema.partial()
  .extend({
    id: z.string(),
    completed_at: z.string().nullable().optional(),
  })
  .extend({
    status: CreateTaskFormSchema.shape.status.removeDefault().optional(),
    priority: CreateTaskFormSchema.shape.priority.removeDefault().optional(),
  });

// Task Notes Schema
export const TaskNoteSchema = z.object({
  id: z.string(),
  task_id: z.string(),
  user_id: z.string(),
  note_type: z.enum(TASK_NOTE_TYPE_VALUES).default("general"),
  content: z.string().min(1, "Note content is required"),
  is_internal: z.boolean().default(false),
  created_at: z.string(),
  updated_at: z.string(),
  user: z
    .object({
      id: z.string(),
      first_name: z.string(),
      last_name: z.string(),
      email: z.string().email(),
    })
    .optional(),
});

// Create Task Note Schema
export const CreateTaskNoteSchema = z.object({
  task_id: z.string(),
  content: z.string().min(1, "Note content is required"),
  note_type: z.enum(TASK_NOTE_TYPE_VALUES).optional().default("general"),
  is_internal: z.boolean().optional().default(false),
});

// Task Filters Schema
export const TaskFiltersSchema = z.object({
  search: z.string().optional(),
  status: z.string().optional(),
  priority: z.string().optional(),
  assigned_users: z.string().optional(),
  contact_id: z.string().optional(),
  due_date_from: z.string().optional(),
  due_date_to: z.string().optional(),
  overdue: z.boolean().optional(),
});

// Webhook Task Schema
export const TaskWebhookSchema = z.object({
  task: z.object({
    title: z.string().min(1, "Task title is required"),
    description: z.string().optional(),
    priority: TaskPrioritySchema.optional().default("medium"),
    due_date: z.string().optional(),
    tags: z.array(z.string()).optional(),
    contactEmail: z.string().email().optional(),
    contact_id: z.string().optional(),
  }),
  assigneeEmail: z.string().email("Valid assignee email is required"),
});

// ============================================================================
// EXPORTED TYPES (inferred from Zod schemas)
// ============================================================================

export type Task = z.infer<typeof TaskSchema>;
export type CreateTaskForm = z.infer<typeof CreateTaskFormSchema>;
export type UpdateTaskForm = z.infer<typeof UpdateTaskFormSchema>;
export type TaskNote = z.infer<typeof TaskNoteSchema>;
export type CreateTaskNote = z.infer<typeof CreateTaskNoteSchema>;
export type TaskFilters = z.infer<typeof TaskFiltersSchema>;
export type TaskWebhookPayload = z.infer<typeof TaskWebhookSchema>;

// ============================================================================
// TASK STATUS AND PRIORITY OPTIONS (for UI components)
// ============================================================================

const TASK_STATUS_META: Record<
  (typeof TASK_STATUS_VALUES)[number],
  { label: string; color: string }
> = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800" },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-800" },
  completed: { label: "Completed", color: "bg-green-100 text-green-800" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800" },
};

const TASK_PRIORITY_META: Record<
  (typeof TASK_PRIORITY_VALUES)[number],
  { label: string; color: string }
> = {
  low: { label: "Low", color: "bg-card-100 text-gray-800" },
  medium: { label: "Medium", color: "bg-blue-100 text-blue-800" },
  high: { label: "High", color: "bg-orange-100 text-orange-800" },
  urgent: { label: "Urgent", color: "bg-red-100 text-red-800" },
};

/** UI options — values from `TASK_STATUS_VALUES`. */
export const TASK_STATUS_OPTIONS = TASK_STATUS_VALUES.map((value) => ({
  value,
  label: TASK_STATUS_META[value].label,
  color: TASK_STATUS_META[value].color,
}));

/** UI options — values from `TASK_PRIORITY_VALUES`. */
export const TASK_PRIORITY_OPTIONS = TASK_PRIORITY_VALUES.map((value) => ({
  value,
  label: TASK_PRIORITY_META[value].label,
  color: TASK_PRIORITY_META[value].color,
}));

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Colour lookup with a neutral default — deliberately accepts any string
 * because callers read `status` straight off a row (nullable in codegen, and
 * `contact_tasks.priority` is plain text), not from a validated union.
 */
export const getTaskStatusColor = (status: TaskStatus | string | null | undefined): string => {
  const option = TASK_STATUS_OPTIONS.find((opt) => opt.value === status);
  return option?.color || "bg-card-100 text-gray-800";
};

/** See {@link getTaskStatusColor} — same tolerance, same neutral default. */
export const getTaskPriorityColor = (
  priority: TaskPriority | string | null | undefined
): string => {
  const option = TASK_PRIORITY_OPTIONS.find((opt) => opt.value === priority);
  return option?.color || "bg-card-100 text-gray-800";
};

export const formatTaskStatus = (status: TaskStatus): string => {
  const option = TASK_STATUS_OPTIONS.find((opt) => opt.value === status);
  return option?.label || status;
};

export const formatTaskPriority = (priority: TaskPriority): string => {
  const option = TASK_PRIORITY_OPTIONS.find((opt) => opt.value === priority);
  return option?.label || priority;
};
